import { expAccountingText, expGainedLabel } from '@/i18n/exp-accounting'
import { widgetCopy } from '@/i18n/widget-copy'
import type { LiveRateSnapshot, SessionRecord } from '@/domain/types'
import type { AppLocale } from '@/i18n/messages'
import { formatRate, type RateUnit } from '@/core/display-settings'
import { rateLabel } from '@/i18n/display-settings'
import { signedCompact } from './widget-format'
import { expAverageLabel, expMeaningCopy } from '@/i18n/exp-meaning'

export interface ExpReadoutPresentation { label: string; description: string; rateLabel?: string; showLabel?: boolean; netUnavailableHint?: string }

export function presentExpReadout(rates: LiveRateSnapshot | null, session: SessionRecord | null, locale: AppLocale, rateUnit: RateUnit = 'hour'): ExpReadoutPresentation {
  const percentage = rates?.percentageExp ?? session?.aggregate.percentageExp
  const point = rates?.expReadout?.lastConfirmed?.point ?? session?.endState?.exp
  if (percentage || point?.estimated) {
    return { label: '', showLabel: false,
      rateLabel: percentage ? widgetCopy(locale, 'Percentage points', '百分点') + (rateUnit === 'hour' ? '/h' : '/min') : expAverageLabel(locale, rateUnit),
      description: '' }
  }
  const partial = rates?.partialExp ?? session?.aggregate.partialExp
  const partialHint = partial ? expAccountingText(locale, 'partialRecoveryHint') : undefined
  if (partial && rates?.expReadout?.scope !== 'recovery') return {
    label: partial.waitingForLevel ? widgetCopy(locale, 'Level pending', '等级待识别') : '',
    rateLabel: expAverageLabel(locale, rateUnit), showLabel: partial.waitingForLevel,
    netUnavailableHint: expAccountingText(locale, 'partialHint'),
    description: expAccountingText(locale, 'partialHint')
      + ` (${partial.gaps.length} ${widgetCopy(locale, 'gaps', '处缺口')})`,
  }
  const readout = rates?.expReadout
  const calculation = rates?.expPerHour.calculationStatus
  const pending = calculation === 'pending-transition' || calculation === 'unresolved-level'
  const status = readout?.status ?? (pending ? 'stale' : 'ready')
  const recovery = readout?.scope === 'recovery'
  const ended = session?.status === 'ended'
  // Brief confirmation is silent; expired readings and recovery scope stay explicit.
  const labels = widgetCopy(locale, { ready: '', updating: '', stale: 'Waiting for readings', recovering: 'Gathering samples' }, { ready: '', updating: '', stale: '等待读数', recovering: '积累样本' })
  let label = labels[status]
  const gcw = session?.presetId === 'gcw'
  if (gcw && status === 'updating') label = widgetCopy(locale, 'Confirming', '正在确认')
  if (ended && recovery && status === 'recovering') label = widgetCopy(locale, 'No rate available', '暂无统计')
  else if (recovery && status !== 'recovering') label = `${widgetCopy(locale, 'Since recovery', '恢复后效率')}${label ? ` · ${label}` : ''}`
  const reasons = widgetCopy(locale, {
    'checking-change': 'Confirming an EXP change.', 'waiting-level': 'Waiting for a fresh level reading.',
    'missing-levels': 'Cross-level EXP evidence is missing.', 'source-unreadable': 'Waiting for a readable game frame.',
    'confirmation-timeout': 'The EXP change could not be confirmed in time.', 'continuity-break': 'Capture continuity was interrupted during confirmation.',
  }, {
    'checking-change': '正在核对经验变化。', 'waiting-level': '等待等级读数。', 'missing-levels': '跨级经验缺失。',
    'source-unreadable': '画面暂不可读，等待新的清晰读数。', 'confirmation-timeout': '经验变化未能在确认时间内完成核对。',
    'continuity-break': '确认期间采集连续性中断。',
  })
  const title = expAverageLabel(locale, rateUnit, recovery)
  const lines = [title, recovery ? expMeaningCopy[locale].recoveryFormula : expAccountingText(locale, 'formula')]
  if (readout?.reason) lines.push(session?.presetId === 'mxdc' && readout.reason === 'source-unreadable'
    ? widgetCopy(locale, 'Waiting for a newly confirmed EXP reading.', '尚未获得新的已确认经验读数，正在等待更新。')
    : reasons[readout.reason])
  else if (pending) lines.push(widgetCopy(locale, 'Waiting for complete EXP evidence.', '等待完整的经验确认信息。'))
  const netUnavailableHint = partialHint ?? (readout?.incomplete ? expAccountingText(locale, 'unavailableHint') : undefined)
  if (netUnavailableHint) lines.push(netUnavailableHint)
  const time = (at: number) => Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(at)
  if (readout?.incompleteSince != null) lines.push(`${widgetCopy(locale, 'First session gap', '首次缺口')}：${time(readout.incompleteSince)}`)
  if (readout?.incompleteReason) lines.push(reasons[readout.incompleteReason])
  if (rates?.expRecovery?.startedAt != null) lines.push(`${widgetCopy(locale, 'Recovery baseline', '恢复起点')}：${time(rates.expRecovery.startedAt)}`)
  const last = readout?.lastConfirmed
  if (last) lines.push(`${widgetCopy(locale, 'Last confirmed', '上次确认')}：${time(last.sourceAt)}\n${formatRate(last.value, locale, rateUnit)} ${rateLabel(locale, rateUnit)} · ${expGainedLabel(locale)} ${signedCompact(last.netGain, locale)}`)
  if (status === 'updating') lines.push(widgetCopy(locale, 'Showing the last confirmed values while checking.', '校验期间显示上次确认值，尚未计入待确认经验。'))
  if (status === 'stale') lines.push(widgetCopy(locale, 'The last reading is too old to show as a current rate.', '上次读数已过期，暂不作为当前效率显示。'))
  if (session && !readout?.lastConfirmed && session.aggregate.expGained === null && !partial?.lastConfirmedExp) {
    return { label: '', description: lines.join('\n'), rateLabel: title, showLabel: false }
  }
  return { label, description: lines.join('\n'), rateLabel: title, ...(netUnavailableHint ? { netUnavailableHint } : {}), ...((gcw || status === 'stale' || status === 'recovering' || recovery) ? { showLabel: true } : {}) }
}
